import {
  Directive,
  ElementRef,
  HostListener,
  input,
  output,
} from "@angular/core";

@Directive({
  selector: "[dateInputKeyboardHandler]",
})
export class DateInputKeyboardHandlerDirective {
  dateFormat = input<string>("DD-MMM-YYYY");
  dateChanged = output<Date>();
  keyboardNavigationEnable = input<boolean>(true);
  private monthAbbreviations = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  private ranges: Record<"day" | "month" | "year", [number, number]> = {
    day: [0, 2],
    month: [3, 6],
    year: [7, 11],
  };
  private isMM: boolean;
  private isMMM: boolean;
  constructor(private el: ElementRef) {}

  @HostListener("click", ["$event"])
  onClick(event: MouseEvent): void {
    event.preventDefault();
    if (!this.keyboardNavigationEnable()) return;
    this.assignRange();
    const node = this.el.nativeElement;
    const start = node.selectionStart || 0;
    let selectionStart = 0;
    let selectionEnd = 2;
    if (start >= this.ranges.month[0] && start <= this.ranges.month[1]) {
      // Month field
      selectionStart = this.ranges.month[0];
      selectionEnd = this.ranges.month[1];
    } else if (start >= this.ranges.year[0]) {
      // Year field
      selectionStart = this.ranges.year[0];
      selectionEnd = this.ranges.year[1];
    }
    node.setSelectionRange(selectionStart, selectionEnd);
    node.focus();
  }

  // Handle keydown events for navigation and date manipulation
  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.keyboardNavigationEnable()) return;
    switch (event.code) {
      case "Tab": // Tab
      case "ArrowRight": // Right arrow
        this.selectionNavigation();
        break;
      case "ArrowLeft": // Left arrow
        this.selectionNavigation(true);
        break;
      case "ArrowUp": // Up arrow
        this.insertAtCaretPos(true);
        break;
      case "ArrowDown": // Down arrow
        this.insertAtCaretPos(false);
        break;
      default:
        if (/[0-9]/.test(event.key)) {
          break;
        }
    }
  }

  // Determine the current field based on cursor position
  private getFieldFromPosition(pos: number): "day" | "month" | "year" {
    if (pos <= this.ranges.day[1]) return "day";
    if (pos <= this.ranges.month[1]) return "month";
    return "year";
  }

  // Navigate to the next or previous field
  private getNextField(
    currentField: "day" | "month" | "year",
    isLeft: boolean
  ): "day" | "month" | "year" {
    const fields: ("day" | "month" | "year")[] = ["day", "month", "year"];
    const index = fields.indexOf(currentField);
    return isLeft
      ? fields[index > 0 ? index - 1 : fields.length - 1]
      : fields[index < fields.length - 1 ? index + 1 : 0];
  }

  // Move cursor between day, month, and year fields
  private selectionNavigation(isLeft = false): void {
    const node = this.el.nativeElement;
    const currentField = this.getFieldFromPosition(node.selectionStart || 0);
    const nextField = this.getNextField(currentField, isLeft);

    const [start, end] = this.ranges[nextField];
    node.setSelectionRange(start, end);
    node.focus();
  }

  // Increment or decrement the date field under the cursor
  private insertAtCaretPos(isUp: boolean): void {
    const node = this.el.nativeElement;
    const value = node.value;
    const selectionStart = node.selectionStart || 0;
    const field = this.getFieldFromPosition(selectionStart);

    const dateParts = this.parseDateString(value);
    this.updateField(field, isUp, dateParts);
    node.value = this.formatDateString(dateParts);
    const [start, end] = this.ranges[field];
    node.setSelectionRange(start, end);
    node.focus();
    const newDate = new Date(
      dateParts.year,
      dateParts.month - 1,
      dateParts.day
    );
    if (!isNaN(newDate.getTime())) {
      this.dateChanged.emit(newDate);
    }
  }

  // Parse the date string into parts
  private parseDateString(value: string): {
    day: number;
    month: number;
    year: number;
  } {
    const monthMap: { [key: string]: number } = {};
    if (this.isMMM) {
      this.monthAbbreviations.forEach((abbr, index) => {
        monthMap[abbr.toUpperCase()] = index + 1;
      });
      const [dayStr, monthAbbr, yearStr] = value.split("-");
      return {
        day: parseInt(dayStr, 10) || 1,
        month: monthMap[monthAbbr.toUpperCase()] || 1,
        year: parseInt(yearStr, 10) || new Date().getFullYear(),
      };
    } else if (this.isMM) {
      const [dayStr, monthStr, yearStr] = value.split("/");
      return {
        day: parseInt(dayStr, 10) || 1,
        month: parseInt(monthStr, 10) || 1,
        year: parseInt(yearStr, 10) || new Date().getFullYear(),
      };
    }
  }

  // Update the day, month, or year based on user input
  private updateField(
    field: "day" | "month" | "year",
    isUp: boolean,
    dateParts: { day: number; month: number; year: number }
  ): void {
    if (field === "day") {
      const lastDate = new Date(dateParts.year, dateParts.month, 0).getDate();
      dateParts.day = isUp
        ? dateParts.day < lastDate
          ? dateParts.day + 1
          : 1
        : dateParts.day > 1
        ? dateParts.day - 1
        : lastDate;
    } else if (field === "month") {
      dateParts.month = isUp
        ? dateParts.month < 12
          ? dateParts.month + 1
          : 1
        : dateParts.month > 1
        ? dateParts.month - 1
        : 12;
      const lastDate = new Date(dateParts.year, dateParts.month, 0).getDate();
      if (dateParts.day > lastDate) dateParts.day = lastDate;
    } else if (field === "year") {
      dateParts.year = isUp ? dateParts.year + 1 : dateParts.year - 1;
      const lastDate = new Date(dateParts.year, dateParts.month, 0).getDate();
      if (dateParts.day > lastDate) dateParts.day = lastDate;
    }
  }

  // Format the date parts back into a string
  private formatDateString(dateParts: {
    day: number;
    month: number;
    year: number;
  }): string {
    if (this.isMMM) {
      const monthAbbr = this.monthAbbreviations[dateParts.month - 1];
      return `${dateParts.day.toString().padStart(2, "0")}-${monthAbbr}-${
        dateParts.year
      }`;
    } else if (this.isMM) {
      return `${dateParts.day.toString().padStart(2, "0")}/${dateParts.month
        .toString()
        .padStart(2, "0")}/${dateParts.year}`;
    }
    const monthAbbr = this.monthAbbreviations[dateParts.month - 1];
    return `${dateParts.day.toString().padStart(2, "0")}-${monthAbbr}-${
      dateParts.year
    }`;
  }
  assignRange() {
    this.isMM = this.dateFormat().toLowerCase().includes("/mm/") ? true : false;
    this.isMMM = this.dateFormat().toLowerCase().includes("-mmm-")
      ? true
      : false;
    if (this.isMM) {
      this.ranges.month = [3, 5];
      this.ranges.year = [6, 10];
    } else if (this.isMMM) {
      this.ranges.month = [3, 6];
      this.ranges.year = [7, 11];
    }
  }
}
